/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

// override to add the wrapper for the total section to follow the general styles

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pluralize from "@monsma/XDL/Components/Common/Pluralize";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const promotions = getPromotionsDataView(state, state.pages.orderApprovalDetails.cartId).value;

    return {
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasCartContext;

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemsCount?: TypographyPresentationProps;
}

export interface OrderApprovalDetailsTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    // custom
    xOrderApprovalTotal?: XStyles;
}

export const orderApprovalDetailsTotalStyles: OrderApprovalDetailsTotalStyles = {};

const styles = orderApprovalDetailsTotalStyles;

const OrderApprovalDetailsTotal = ({ cart, orderPromotions, shippingPromotions, discountTotal }: Props) => {
    const styles = mergeToNew(orderApprovalDetailsTotalStyles, orderApprovalDetailsTotalStyles.xOrderApprovalTotal);

    if (!cart) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{translate("Order Summary")}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.itemsCount}>
                    <Pluralize
                        total={cart?.cartLines?.length || 0}
                        pluralLabel={translate("Items")}
                        singularLabel={translate("Item")}
                    />
                </Typography>
                <CartTotalDisplay
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                    extendedStyles={styles.cartTotal}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCart(OrderApprovalDetailsTotal)),
    definition: {
        group: "Order Approval Details",
        allowedContexts: ["OrderApprovalDetailsPage"],
        displayName: "Order Approval Total",
    },
};

export default widgetModule;
